import React from "react";
import { Cell, Row } from "@tanstack/react-table";
import { CommandAction } from "@/components/command-bar/command-bar-context";
import {
  ContextConfig,
  useCommandBarShortcuts,
} from "@/components/command-bar/use-command-bar-context";
import { useMouseEvent } from "@/hooks/use-mouse-event";

// 1. The Generic Return Type Definition
export type UseRowHoveringReturn<TData> = {
  row: Row<TData> | null;
  cell: Cell<TData, unknown> | null;
  handleMouseEnter: (args: {
    row: Row<TData>;
    cell: Cell<TData, unknown>;
  }) => void;
  handleMouseLeave: () => void;
};
export type UseRowHoveringProps<TData> = {
  getRowActions?: (row: Row<TData>) => ContextConfig;
};

// 2. The Generic Hook
// TData defaults to 'unknown' so it doesn't break if you don't pass a type
export const useRowHovering = <TData = unknown,>({
  getRowActions,
}: UseRowHoveringProps<TData>): UseRowHoveringReturn<TData> => {
  const [hoveredRow, setHoveredRow] = React.useState<Row<TData> | null>(null);
  const [hoveredCell, setHoveredCell] = React.useState<Cell<
    TData,
    unknown
  > | null>(null);

  const { setRowContext, clearRowContext, isOpen } = useCommandBarShortcuts();

  const handleMouseEnter = ({
    row,
    cell,
  }: {
    row: Row<TData>;
    cell: Cell<TData, unknown>;
  }) => {
    setHoveredCell(cell);
    setHoveredRow(row);

    if (getRowActions) {
      const actions = getRowActions(row);
      setRowContext(actions);
    }
  };

  const handleMouseLeave = () => {
    setHoveredCell(null);
    setHoveredRow(null);
    if (!isOpen) clearRowContext();
  };

  return {
    row: hoveredRow,
    cell: hoveredCell,
    handleMouseEnter,
    handleMouseLeave,
  };
};
